import { t } from 'i18next'
import { Control, FieldValues, useWatch } from 'react-hook-form'

import { CharacterCounter } from '@/Atomic/Atoms/CharacterCounter/CharacterCounter'
import { Textarea } from '@/Atomic/Molecules/Inputs/Textarea'

type BaseProps = Omit<
  React.ComponentProps<typeof Textarea>,
  'invalid' | 'labelRight' | 'maxLength'
>

export type CharacterLimitedTextareaProps = BaseProps & {
  characterLimit?: number
  enableCharacterLimit?: boolean
  control?: Control<FieldValues>
}

export const CharacterLimitedTextarea: React.FC<
  CharacterLimitedTextareaProps
> = ({
  characterLimit,
  enableCharacterLimit = false,
  control,
  ...textareaProps
}) => {
  const fieldError = textareaProps.error
  const canEdit = !textareaProps.disabled

  const watchedValue = useWatch({
    control,
    name: textareaProps.name,
  })

  const currentValue =
    typeof watchedValue === 'string'
      ? watchedValue
      : typeof textareaProps.value === 'string'
        ? textareaProps.value
        : typeof textareaProps.defaultValue === 'string'
          ? textareaProps.defaultValue
          : watchedValue == null
            ? ''
            : String(watchedValue)

  const count = currentValue.length
  const isActive = typeof characterLimit === 'number' && characterLimit > 0
  const isOverLimit = isActive && count > (characterLimit as number)

  const showCounter = enableCharacterLimit && isActive
  const enforceLimit = showCounter && canEdit

  const labelRight = showCounter ? (
    <CharacterCounter
      count={count}
      maxLength={characterLimit as number}
      isOverLimit={false}
    />
  ) : undefined

  const characterLimitError =
    enforceLimit && isOverLimit
      ? t('errors.validation.characterLimit')
      : undefined

  return (
    <Textarea
      {...textareaProps}
      invalid={Boolean(fieldError) || (enforceLimit && isOverLimit)}
      error={fieldError || characterLimitError}
      labelRight={labelRight}
    />
  )
}
